import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { CheckCircle, Plus, Calendar, DollarSign, Users, Trash2, X, Link2, BellRing, Receipt, FileText, FileSpreadsheet } from 'lucide-react';
import { StatusPill, SwissTable, SwissTH, SwissTD, SwissCard, EmptyState, Overline } from '../layout/SwissUI';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import TripDetailsModal from './TripDetailsModal';

const API = process.env.REACT_APP_BACKEND_URL;
const f = (n) => `$${Number(n || 0).toLocaleString()}`;

const VEHICLES = ['Concierge SUV', 'Concierge Sedan', 'Sprinter Van', 'Rolls-Royce Cullinan', 'Rolls-Royce Ghost', 'Maybach GLS', 'Maybach Sedan', 'Lamborghini Urus', 'Bentley Bentayga', 'Bentley Flying Spur', 'Lamborghini Huracan'];
const EVENT_TYPES = ['Wedding & Bridal', 'Celebration', 'Corporate & Travel', 'Entertainment', 'Memorial', 'Media & Production'];

const BookingsVIPTab = () => {
  const [tab, setTab] = useState('bookings');
  const [bookings, setBookings] = useState([]);
  const [members, setMembers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [form, setForm] = useState({ client_name: '', event_date: '', pickup_time: '', pickup_address: '', dropoff_address: '', vehicle: '', driver_id: '', event_type: '', hours: 3, total: 0, deposit: 0, notes: '' });
  const [memberForm, setMemberForm] = useState({ name: '', email: '', phone: '', tier: 'member' });
  const [detailsBooking, setDetailsBooking] = useState(null);
  const navigate = useNavigate();
  const openClient = (phone, email, name) => {
    const key = (phone || '').replace(/\D/g, '').slice(-10) || (email || '').toLowerCase() || (name || '').toLowerCase();
    if (key) navigate(`/admin/command-center?tab=clients&client=${encodeURIComponent(key)}`);
  };

  const loadData = () => {
    fetch(`${API}/api/command-center/confirmed-bookings`).then(r => r.json()).then(d => setBookings(d.bookings || []));
    fetch(`${API}/api/command-center/vip-members`).then(r => r.json()).then(d => setMembers(d.members || []));
    fetch(`${API}/api/command-center/drivers`).then(r => r.json()).then(d => setDrivers(d.drivers || []));
  };
  useEffect(loadData, []);

  const createBooking = async () => {
    await fetch(`${API}/api/command-center/confirmed-bookings`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setShowForm(false); setForm({ client_name: '', event_date: '', pickup_time: '', pickup_address: '', dropoff_address: '', vehicle: '', driver_id: '', event_type: '', hours: 3, total: 0, deposit: 0, notes: '' }); loadData();
  };

  const updateStatus = async (id, status) => {
    await fetch(`${API}/api/command-center/confirmed-bookings/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cc_status: status }) });
    loadData();
  };

  const deleteBooking = async (id) => {
    if (!window.confirm(`Delete booking ${id}? It will be moved to the archive and can be restored.`)) return;
    await fetch(`${API}/api/command-center/confirmed-bookings/${id}`, { method: 'DELETE' });
    loadData();
  };

  const copyDriverLink = async (b) => {
    try {
      const r = await fetch(`${API}/api/ops/bookings/${b.booking_id}/trip-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
      const d = await r.json();
      if (!d.trip_url) throw new Error('Failed to generate link');
      await navigator.clipboard.writeText(d.trip_url).catch(() => {});
      if (b.driver_id && b.driver_id !== 'unassigned' && window.confirm(`Trip link copied.\n\nText the trip sheet to the assigned driver now?`)) {
        const sr = await fetch(`${API}/api/ops/bookings/${b.booking_id}/send-driver-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) });
        const sd = await sr.json();
        if (sr.ok) alert(`Trip sheet texted to driver (${sd.sent_to})`);
        else alert(sd.detail || 'SMS failed');
      } else {
        alert(`Driver trip link copied:\n${d.trip_url}`);
      }
    } catch (e) { alert(e.message); }
  };

  const [busyDocId, setBusyDocId] = useState(null);

  const createContractFromBooking = async (b) => {
    if (busyDocId) return;
    setBusyDocId(b.booking_id);
    try {
      const r = await fetch(`${API}/api/command-center/bookings/${b.booking_id}/create-contract`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Contract ${d.contract_id} created for ${b.client_name || 'client'} — see Contracts & Calendar tab`);
    } catch (e) { toast.error(e.message); }
    setBusyDocId(null);
  };

  const createInvoiceFromBooking = async (b) => {
    if (busyDocId) return;
    setBusyDocId(b.booking_id);
    try {
      const r = await fetch(`${API}/api/command-center/bookings/${b.booking_id}/create-invoice`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Invoice ${d.invoice_id} created for ${b.client_name || 'client'} — see Contracts & Calendar tab`);
    } catch (e) { toast.error(e.message); }
    setBusyDocId(null);
  };

  const sendReminder = async (b) => {
    if (!b.client_phone) { alert('Booking has no client phone'); return; }
    if (!window.confirm(`Text a trip reminder to ${b.client_name} (${b.client_phone})?`)) return;
    const r = await fetch(`${API}/api/ops/bookings/${b.booking_id}/send-reminder`, { method: 'POST' });
    const d = await r.json();
    alert(r.ok ? `Reminder sent to ${d.sent_to}` : (d.detail || 'SMS failed'));
  };

  const createMember = async () => {
    await fetch(`${API}/api/command-center/vip-members`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(memberForm) });
    setShowMemberForm(false); setMemberForm({ name: '', email: '', phone: '', tier: 'member' }); loadData();
  };

  const filteredBookings = filter === 'all' ? bookings : bookings.filter(b => b.cc_status === filter);
  const stats = { total: bookings.length, upcoming: bookings.filter(b => b.cc_status === 'upcoming').length, revenue: bookings.reduce((s, b) => s + (b.total || 0), 0), deposits: bookings.reduce((s, b) => s + (b.deposit || 0), 0) };

  return (
    <div data-testid="bookings-vip-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Bookings & VIP Members</h2>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex items-center justify-between mb-4">
          <TabsList className="bg-white border"><TabsTrigger value="bookings">Bookings</TabsTrigger><TabsTrigger value="members">VIP Members</TabsTrigger></TabsList>
          {tab === 'bookings' ? <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700 text-xs h-8" data-testid="new-booking-btn"><Plus className="h-3 w-3 mr-1" /> New Booking</Button>
            : <Button size="sm" onClick={() => setShowMemberForm(true)} className="bg-red-600 hover:bg-red-700 text-xs h-8"><Plus className="h-3 w-3 mr-1" /> Add Member</Button>}
        </div>

        <TabsContent value="bookings">
          {showForm && (
            <Card className="p-4 border-gray-200 mb-4">
              <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">New Confirmed Booking</h3><button onClick={() => setShowForm(false)}><X className="h-4 w-4 text-gray-400" /></button></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <Input placeholder="Client Name" value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} className="h-9 text-xs" />
                <Input type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))} className="h-9 text-xs" />
                <Input type="time" value={form.pickup_time} onChange={e => setForm(p => ({ ...p, pickup_time: e.target.value }))} className="h-9 text-xs" />
                <Input placeholder="Pickup Address" value={form.pickup_address} onChange={e => setForm(p => ({ ...p, pickup_address: e.target.value }))} className="h-9 text-xs" />
                <Input placeholder="Drop-off Address" value={form.dropoff_address} onChange={e => setForm(p => ({ ...p, dropoff_address: e.target.value }))} className="h-9 text-xs" />
                <Select value={form.vehicle} onValueChange={v => setForm(p => ({ ...p, vehicle: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Vehicle" /></SelectTrigger><SelectContent>{VEHICLES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent></Select>
                <Select value={form.driver_id} onValueChange={v => setForm(p => ({ ...p, driver_id: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Assign Driver" /></SelectTrigger><SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{drivers.map(d => <SelectItem key={d.driver_id} value={d.driver_id}>{d.name}</SelectItem>)}</SelectContent></Select>
                <Select value={form.event_type} onValueChange={v => setForm(p => ({ ...p, event_type: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Event Type" /></SelectTrigger><SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                <Input type="number" placeholder="Hours" value={form.hours} onChange={e => setForm(p => ({ ...p, hours: parseInt(e.target.value) || 0 }))} className="h-9 text-xs" />
                <Input type="number" placeholder="Total $" value={form.total || ''} onChange={e => setForm(p => ({ ...p, total: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
                <Input type="number" placeholder="Deposit $" value={form.deposit || ''} onChange={e => setForm(p => ({ ...p, deposit: parseFloat(e.target.value) || 0 }))} className="h-9 text-xs" />
                <Input placeholder="Notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="h-9 text-xs lg:col-span-3" />
              </div>
              <div className="flex gap-2 mt-3"><Button size="sm" onClick={createBooking} className="bg-red-600 hover:bg-red-700 text-xs h-8">Confirm Booking</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)} className="text-xs h-8">Cancel</Button></div>
            </Card>
          )}

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {[{ l: 'Total Bookings', v: stats.total, i: Calendar }, { l: 'Upcoming', v: stats.upcoming, i: Calendar }, { l: 'Revenue Booked', v: f(stats.revenue), i: DollarSign }, { l: 'Deposits Collected', v: f(stats.deposits), i: DollarSign }].map(s => (
              <Card key={s.l} className="p-3 border-gray-200 text-center"><s.i className="h-4 w-4 mx-auto text-gray-400 mb-1" /><p className="text-lg font-bold text-gray-900">{s.v}</p><p className="text-[10px] text-gray-500">{s.l}</p></Card>
            ))}
          </div>

          <div className="flex gap-1 mb-3">
            {['all', 'upcoming', 'completed', 'cancelled'].map(f2 => (
              <button
                key={f2}
                onClick={() => setFilter(f2)}
                className={`px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] border transition-colors ${filter === f2 ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#666] border-[#E5E5E5] hover:border-[#0A0A0A]'}`}
                data-testid={`filter-${f2}`}
              >{f2}</button>
            ))}
          </div>

          {filteredBookings.length === 0 ? (
            <EmptyState
              icon={Calendar}
              title="No bookings in this view"
              description="Try changing the filter above or add a new booking."
              testid="bookings-empty"
            />
          ) : (
            <SwissCard className="overflow-hidden">
              <SwissTable>
                <thead>
                  <tr>
                    <SwissTH className="w-[110px]">Date</SwissTH>
                    <SwissTH>Client</SwissTH>
                    <SwissTH className="hidden md:table-cell">Vehicle</SwissTH>
                    <SwissTH className="hidden lg:table-cell">Route</SwissTH>
                    <SwissTH className="text-right w-[100px]">Total</SwissTH>
                    <SwissTH className="text-center w-[110px]">Status</SwissTH>
                    <SwissTH className="w-[140px]"></SwissTH>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.map(b => (
                    <tr
                      key={b.booking_id}
                      onClick={() => openClient(b.client_phone, b.client_email, b.client_name)}
                      className="hover:bg-[#F9F9FA] cursor-pointer transition-colors"
                      data-testid={`booking-row-${b.booking_id}`}
                    >
                      <SwissTD>
                        <p className="text-sm font-bold">{b.event_date || '—'}</p>
                        {b.pickup_time && <p className="text-[10px] text-[#666] font-mono">{b.pickup_time}</p>}
                      </SwissTD>
                      <SwissTD>
                        <p className="text-sm font-bold hover:text-[#E52B20] truncate max-w-[180px]" data-testid={`booking-client-${b.booking_id}`}>{b.client_name}</p>
                        {b.event_type && <p className="text-[10px] text-[#666]">{b.event_type}</p>}
                      </SwissTD>
                      <SwissTD className="hidden md:table-cell">
                        <p className="text-xs text-[#0A0A0A] truncate max-w-[180px]">{b.vehicle || '—'}</p>
                        {b.hours ? <p className="text-[10px] text-[#666]">{b.hours}hr</p> : null}
                      </SwissTD>
                      <SwissTD className="hidden lg:table-cell">
                        {b.pickup_address ? (
                          <p className="text-[11px] text-[#666] truncate max-w-[240px]">{b.pickup_address}{b.dropoff_address ? ` → ${b.dropoff_address}` : ''}</p>
                        ) : <span className="text-[11px] text-[#E5E5E5]">—</span>}
                      </SwissTD>
                      <SwissTD className="text-right">
                        <p className="text-sm font-black font-mono">{f(b.total)}</p>
                        {b.deposit > 0 && <p className="text-[9px] text-[#039855] font-mono">Dep {f(b.deposit)}</p>}
                      </SwissTD>
                      <SwissTD className="text-center" onClick={e => e.stopPropagation()}>
                        <InlineStatusEditor
                          status={b.cc_status}
                          onChange={(s) => updateStatus(b.booking_id, s)}
                          testid={`status-edit-${b.booking_id}`}
                        />
                      </SwissTD>
                      <SwissTD onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1 justify-end">
                          {b.cc_status === 'upcoming' && (
                            <>
                              <button onClick={() => updateStatus(b.booking_id, 'completed')} className="px-2 py-1 border border-[#039855] text-[#039855] text-[9px] font-bold uppercase tracking-[0.1em] hover:bg-[#039855] hover:text-white transition-colors" data-testid={`booking-complete-${b.booking_id}`}>✓ Done</button>
                              <button onClick={() => updateStatus(b.booking_id, 'cancelled')} className="px-2 py-1 border border-[#E52B20] text-[#E52B20] text-[9px] font-bold uppercase tracking-[0.1em] hover:bg-[#E52B20] hover:text-white transition-colors" data-testid={`booking-cancel-${b.booking_id}`}>Cancel</button>
                            </>
                          )}
                          <button onClick={() => setDetailsBooking(b)} className="p-1 border border-[#E5E5E5] text-[#1D4ED8] hover:bg-blue-50 transition-colors" title="Trip Details+ (flight, extras, farm-out)" data-testid={`booking-details-${b.booking_id}`}><Receipt className="h-3 w-3" /></button>
                          <button onClick={() => copyDriverLink(b)} className="p-1 border border-[#E5E5E5] text-[#B45309] hover:bg-amber-50 transition-colors" title="Driver trip link (clock in/out)" data-testid={`booking-triplink-${b.booking_id}`}><Link2 className="h-3 w-3" /></button>
                          <button onClick={() => sendReminder(b)} className="p-1 border border-[#E5E5E5] text-[#15803D] hover:bg-green-50 transition-colors" title="SMS reminder to client" data-testid={`booking-remind-${b.booking_id}`}><BellRing className="h-3 w-3" /></button>
                          <button onClick={() => createContractFromBooking(b)} className="p-1 border border-[#E5E5E5] text-[#7C3AED] hover:bg-purple-50 transition-colors" title="Create Contract (auto-filled from this booking)" data-testid={`booking-contract-${b.booking_id}`}><FileText className="h-3 w-3" /></button>
                          <button onClick={() => createInvoiceFromBooking(b)} className="p-1 border border-[#E5E5E5] text-[#0F766E] hover:bg-teal-50 transition-colors" title="Create Invoice (auto-filled from this booking)" data-testid={`booking-invoice-${b.booking_id}`}><FileSpreadsheet className="h-3 w-3" /></button>
                          <button onClick={() => deleteBooking(b.booking_id)} className="p-1 border border-[#E5E5E5] text-[#E52B20] hover:bg-red-50 transition-colors" title="Delete" data-testid={`booking-delete-${b.booking_id}`}><Trash2 className="h-3 w-3" /></button>
                        </div>
                      </SwissTD>
                    </tr>
                  ))}
                </tbody>
              </SwissTable>
              <div className="px-3 py-2 bg-[#F9F9FA] border-t border-[#E5E5E5] text-[10px] text-[#666] uppercase tracking-[0.15em] font-bold flex items-center justify-between">
                <span>{filteredBookings.length} {filteredBookings.length === 1 ? 'booking' : 'bookings'}</span>
                <span>Click any row to open Client 360°</span>
              </div>
            </SwissCard>
          )}
        </TabsContent>

        <TabsContent value="members">
          {showMemberForm && (
            <Card className="p-4 border-gray-200 mb-4">
              <div className="flex justify-between mb-3"><h3 className="text-sm font-bold">Add VIP Member</h3><button onClick={() => setShowMemberForm(false)}><X className="h-4 w-4 text-gray-400" /></button></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Input placeholder="Full Name" value={memberForm.name} onChange={e => setMemberForm(p => ({ ...p, name: e.target.value }))} className="h-9 text-xs" />
                <Input placeholder="Email" value={memberForm.email} onChange={e => setMemberForm(p => ({ ...p, email: e.target.value }))} className="h-9 text-xs" />
                <Input placeholder="Phone" value={memberForm.phone} onChange={e => setMemberForm(p => ({ ...p, phone: e.target.value }))} className="h-9 text-xs" />
                <Select value={memberForm.tier} onValueChange={v => setMemberForm(p => ({ ...p, tier: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="member">Member (-15%)</SelectItem><SelectItem value="affiliate">Affiliate (-10%)</SelectItem><SelectItem value="vip">VIP</SelectItem></SelectContent></Select>
              </div>
              <div className="flex gap-2 mt-3"><Button size="sm" onClick={createMember} className="bg-red-600 hover:bg-red-700 text-xs h-8">Add Member</Button><Button size="sm" variant="outline" onClick={() => setShowMemberForm(false)} className="text-xs h-8">Cancel</Button></div>
            </Card>
          )}

          <div className="grid grid-cols-2 gap-3 mb-4">
            <Card className="p-3 border-gray-200 text-center"><Users className="h-4 w-4 mx-auto text-gray-400 mb-1" /><p className="text-lg font-bold text-gray-900">{members.length}</p><p className="text-[10px] text-gray-500">Total Members</p></Card>
            <Card className="p-3 border-gray-200 text-center"><DollarSign className="h-4 w-4 mx-auto text-gray-400 mb-1" /><p className="text-lg font-bold text-gray-900">{f(members.reduce((s, m) => s + (m.total_spend || 0), 0))}</p><p className="text-[10px] text-gray-500">Total Member Spend</p></Card>
          </div>

          {members.length === 0 ? (
            <Card className="p-8 text-center border-gray-200"><p className="text-sm text-gray-400">No VIP members yet.</p></Card>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <Card key={m.member_id} className="p-3 border-gray-200 flex items-center justify-between hover:border-red-200 transition-colors">
                  <div>
                    <button
                      onClick={() => openClient(m.phone, m.email, m.name)}
                      className="text-sm font-bold text-gray-900 hover:text-red-600 underline-offset-2 hover:underline"
                      data-testid={`vip-member-${m.member_id}`}
                    >
                      {m.name}
                    </button>
                    <p className="text-[10px] text-gray-500">{m.email} | {m.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-[9px] font-bold capitalize ${m.tier === 'vip' ? 'bg-purple-100 text-purple-700' : m.tier === 'affiliate' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>{m.tier}</span>
                    <span className="text-xs font-semibold text-gray-700">{m.total_bookings || 0} trips | {f(m.total_spend || 0)}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      {detailsBooking && <TripDetailsModal booking={detailsBooking} onClose={() => setDetailsBooking(null)} onSaved={loadData} />}
    </div>
  );
};

export default BookingsVIPTab;

// ── Inline status editor — click pill to swap to dropdown, blur to commit ──
function InlineStatusEditor({ status, onChange, testid }) {
  const [editing, setEditing] = useState(false);
  const statusMap = {
    upcoming:  { variant: 'pending',  label: 'Upcoming' },
    completed: { variant: 'success',  label: 'Completed' },
    cancelled: { variant: 'alert',    label: 'Cancelled' },
    active:    { variant: 'active',   label: 'Active' },
    void:      { variant: 'muted',    label: 'Void' },
  };
  const meta = statusMap[status] || { variant: 'muted', label: status || '—' };

  if (editing) {
    return (
      <select
        autoFocus
        value={status}
        onChange={(e) => { onChange(e.target.value); setEditing(false); }}
        onBlur={() => setEditing(false)}
        className="border border-[#0A0A0A] bg-white px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold focus:outline-none"
        data-testid={testid}
      >
        {Object.keys(statusMap).map(k => (
          <option key={k} value={k}>{statusMap[k].label}</option>
        ))}
      </select>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
      title="Click to edit status"
      data-testid={`${testid}-pill`}
    >
      <StatusPill variant={meta.variant}>{meta.label}</StatusPill>
    </button>
  );
}
